import { css } from '@emotion/react';
import { Paper } from '@mui/material';
import { Redirect, Switch, useLocation } from 'react-router-dom';

import { Box, Route, RouteLayout, Typography } from 'shared/components/ui';
import { ROUTES } from 'shared/constants';
import { SearchProvider } from 'shared/features/search';
import { useFeatureEnabled } from 'shared/hooks';

import {
  SettingsSchoolContentModeration,
  SettingsCommunityRewarding,
  SettingsCommunityContentRestrictions,
} from '../components';
import { CustomizationContent } from '../components/Customization';
import { MainNavigationWrapper } from '../components/MainNavigation/MainNavigationWrapper/MainNavigationWrapper';
import { Roles } from '../components/Roles';
import { SettingsSidebar } from '../components/SettingsSidebar';
import { SiteAdmins } from '../components/SiteAdmins/SiteAdmins';
import { SiteSettings } from '../components/SiteSettings/SiteSettings';
import { TopBarSettings } from '../components/TopBar/TopBarSettings';
import { SettingsCommunityProducts } from './SettingsCommunityProducts';

const routeLayoutCss = css`
  width: 100%;
  padding: 2rem;
`;

const boxCss = css`
  max-width: 100%;
`;

const titleCss = css`
  margin-bottom: 30px;
`;

const contentCss = css`
  display: grid;
  grid-template-columns: 1fr 4fr;
  min-height: 85vh;
`;

const contentContainerCSS = css`
  min-width: 0;
`;

export function SettingsPage() {
  const location = useLocation();
  const isRootRoute = location.pathname === ROUTES.admin.settings.index;

  const { enabled: showRewardingSettings } = useFeatureEnabled('rewardActions');
  const { enabled: showContentRestrictionsSettings } = useFeatureEnabled('contentRestrictions');
  const { enabled: showCommunityProductsSettings } = useFeatureEnabled('communityMarketplace');

  return (
    <RouteLayout css={routeLayoutCss}>
      <Box display="flex" flexDirection="column" flexGrow={1} css={boxCss}>
        <Typography css={titleCss} variant="subtitle2" color="primary" align="left">
          Settings
        </Typography>

        <Paper css={contentCss} elevation={1} square={false} variant="elevation">
          <SettingsSidebar />

          <Box css={contentContainerCSS}>
            <Switch>
              <Route path={ROUTES.admin.settings.navigation} authAccess="any" exact>
                <MainNavigationWrapper />
              </Route>

              <Route path={ROUTES.admin.settings.siteSettings} authAccess="any" exact>
                <SiteSettings />
              </Route>

              <Route path={ROUTES.admin.settings.roles} authAccess="any" exact>
                <Roles />
              </Route>

              <Route path={ROUTES.admin.settings.siteAdmins} authAccess="any" exact>
                <SearchProvider>
                  <SiteAdmins />
                </SearchProvider>
              </Route>

              <Route path={ROUTES.admin.settings.customization} authAccess="any" exact>
                <CustomizationContent />
              </Route>

              <Route path={ROUTES.admin.settings.news} authAccess="any" exact>
                <TopBarSettings />
              </Route>

              <Route path={ROUTES.admin.settings.contentModeration} authAccess="private" exact>
                <SettingsSchoolContentModeration />
              </Route>

              {showRewardingSettings && (
                <Route path={ROUTES.admin.settings.rewarding} authAccess="private" exact>
                  <SettingsCommunityRewarding />
                </Route>
              )}

              {showContentRestrictionsSettings && (
                <Route path={ROUTES.admin.settings.contentRestrictions} authAccess="private" exact>
                  <SettingsCommunityContentRestrictions />
                </Route>
              )}

              {showCommunityProductsSettings && (
                <Route path={ROUTES.admin.settings.communityProducts} authAccess="private" exact>
                  <SettingsCommunityProducts />
                </Route>
              )}

              <Route path="*" authAccess="any">
                <Redirect to={ROUTES.admin.settings.siteSettings} />
              </Route>
            </Switch>
          </Box>

          {isRootRoute && <Redirect to={ROUTES.admin.settings.siteSettings} />}
        </Paper>
      </Box>
    </RouteLayout>
  );
}
